import type { GetStaticPaths, GetStaticProps } from 'next'
import { paths } from '@/constants'
import { Durations } from '@/constants/durations'
import { NextPageWithLayout } from '@/layouts'
import { ToastProvider } from '@/molecules/Toast/ToastProvider'
import { Page, PageSeo } from '@/page'
import { getPageByPath } from '@/services/CmsService/Pages'
import { PageDataContext, PageModel, fetchPageDataContext } from '@/services/RenderService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'

interface CategoryPageProps {
  pageDataContext: PageDataContext
  page: PageModel
  locale: string
}

const CategoryPage: NextPageWithLayout<CategoryPageProps> = ({ page, pageDataContext, locale }) => {
  return (
    <>
      <PageSeo page={page} locale={locale} />
      <div className="bg-core-gray-950 pb-10 pt-28 text-lg text-white">
        <main className="flex flex-col gap-8">
          <ToastProvider>
            <Page page={page} pageDataContext={{ ...pageDataContext, contentItemConfig: { padding: true } }} />
          </ToastProvider>
        </main>
      </div>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async (context) =>
  getStaticPropsErrorHandler(
    { context, filePath: 'pages/[locale]/category/index.tsx' },
    async ({ locale, next404, preview }) => {
      const page = await getPageByPath(paths.category.index, { locale, preview })
      const pageDataContext = page ? await fetchPageDataContext(page, preview, locale) : null

      if (!page || !pageDataContext) {
        return next404(Durations.FIVE_MINUTES_IN_SECONDS)
      }

      return {
        props: {
          pageDataContext: pageDataContext,
          page,
          locale,
          ...(await loadTranslations(locale, ['common', 'account', 'category'])),
        },
        revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
      }
    },
  )

export default CategoryPage
